import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { VARIANTS, CATEGORIES, mergeCategories } from "@/lib/constants";
import PosterCard from "@/components/PosterCard";
import PosterFormDialog from "@/components/PosterFormDialog";
import PosterDetail from "@/components/PosterDetail";
import { Plus, Search } from "lucide-react";

const sortPosters = (list, sort) => {
  const arr = [...list];
  const num = (v) => (v == null ? -Infinity : Number(v));
  switch (sort) {
    case "value":
      return arr.sort((a, b) => num(b.market_value) - num(a.market_value));
    case "roi":
      return arr.sort((a, b) =>
        ((b.market_value ?? 0) - (b.purchase_price ?? 0)) - ((a.market_value ?? 0) - (a.purchase_price ?? 0)));
    case "artist":
      return arr.sort((a, b) => (a.poster_artist || a.band || "").localeCompare(b.poster_artist || b.band || ""));
    case "show":
      return arr.sort((a, b) => (b.show_date || "").localeCompare(a.show_date || ""));
    default:
      return arr; // newest (server order)
  }
};

export default function Collection() {
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [variant, setVariant] = useState("");
  const [category, setCategory] = useState("");
  const [framed, setFramed] = useState("");
  const [sort, setSort] = useState("newest");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [catOptions, setCatOptions] = useState(CATEGORIES);
  const [catMeta, setCatMeta] = useState({});

  const refreshCats = useCallback(() => {
    api.get("/posters/categories").then((r) => setCatOptions(mergeCategories(r.data))).catch(() => {});
    api.get("/categories/meta").then((r) => {
      const m = {};
      r.data.forEach((d) => { m[d.slug] = d; });
      setCatMeta(m);
    }).catch(() => {});
  }, []);

  useEffect(() => { refreshCats(); }, [refreshCats]);

  const fetchPosters = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (variant) params.variant = variant;
    if (category) params.category = category;
    if (framed) params.framed = framed;
    const { data } = await api.get("/posters", { params });
    setPosters(data);
    setLoading(false);
  }, [search, variant, category, framed]);

  useEffect(() => {
    const t = setTimeout(fetchPosters, 250);
    return () => clearTimeout(t);
  }, [fetchPosters]);

  const openAdd = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setSelected(null); setEditing(p); setFormOpen(true); };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">The Archive</div>
          <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">COLLECTION</h1>
          <div className="font-mono text-xs text-muted-gray mt-2">{posters.length} posters</div>
        </div>
        <button onClick={openAdd} data-testid="add-poster-button"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-5 py-3 hard-shadow">
          <Plus size={16} /> Add Poster
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-gray" />
          <input data-testid="collection-search-input" placeholder="Search band, artist, city..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-ink border border-stroke pl-9 pr-3 py-2.5 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid" />
        </div>
        <select data-testid="collection-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-ink border border-stroke px-3 py-2.5 font-mono text-xs uppercase text-paper focus:outline-none focus:border-acid">
          <option value="">All Types</option>
          {catOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select data-testid="collection-variant-filter" value={variant} onChange={(e) => setVariant(e.target.value)}
          className="bg-ink border border-stroke px-3 py-2.5 font-mono text-xs uppercase text-paper focus:outline-none focus:border-acid">
          <option value="">All Variants</option>
          {VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        <select data-testid="collection-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-ink border border-stroke px-3 py-2.5 font-mono text-xs uppercase text-paper focus:outline-none focus:border-acid">
          <option value="newest">Newest</option>
          <option value="value">Value ↓</option>
          <option value="roi">Top ROI</option>
          <option value="artist">Artist A–Z</option>
          <option value="show">Show Date ↓</option>
        </select>
        <select data-testid="collection-framed-filter" value={framed} onChange={(e) => setFramed(e.target.value)}
          className="bg-ink border border-stroke px-3 py-2.5 font-mono text-xs uppercase text-paper focus:outline-none focus:border-acid">
          <option value="">All</option>
          <option value="true">Framed</option>
          <option value="false">Unframed</option>
        </select>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-muted-gray animate-pulse">Loading vault...</div>
      ) : posters.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center">
          <p className="font-mono text-sm text-muted-gray">No posters found. Add your first grail.</p>
          <button onClick={openAdd} className="mt-4 font-mono text-xs uppercase bg-acid text-ink font-bold px-5 py-3">Add Poster</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sortPosters(posters, sort).map((p, i) => (
            <PosterCard key={p.id} poster={p} index={i} meta={catMeta[p.category]}
              onClick={() => { setSelected(p); setSelectedIdx(i); }} />
          ))}
        </div>
      )}

      <PosterFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={() => { fetchPosters(); refreshCats(); }} editing={editing} />
      {selected && (
        <PosterDetail poster={selected} index={selectedIdx}
          onClose={() => setSelected(null)} onEdit={openEdit} onDeleted={fetchPosters} />
      )}
    </div>
  );
}
